import { useEffect, useState } from "react";
import { motion } from "motion/react";
import type { GameKind } from "@minigames/shared";
import { api } from "../lib/api.js";
import { haptic } from "../lib/telegram.js";
import { useStore } from "../lib/store.js";

const GAME_NAME: Record<GameKind, string> = {
  chess: "Шахматы",
  durak: "Дурак",
  checkers: "Шашки",
  reversi: "Реверси",
  gomoku: "Пять в ряд",
  seabattle: "Морской бой",
};

const REASON: Record<string, string> = {
  checkmate: "мат",
  resignation: "сдача",
  timeout: "время",
  stalemate: "пат",
  agreement: "по согласию",
  insufficient_material: "мало материала",
  threefold_repetition: "повторение",
  fifty_moves: "50 ходов",
  timeout_vs_insufficient_material: "время, но матовать нечем",
  durak: "остался с картами",
  both_out: "оба вышли",
  five_in_a_row: "пять в ряд",
  board_full: "доска заполнена",
  more_discs: "больше фишек",
  equal_discs: "поровну",
  no_pieces: "не осталось шашек",
  no_moves: "нечем ходить",
  fleet_destroyed: "флот уничтожен",
  quiet_moves_limit: "без взятий",
};

interface Row {
  id: string;
  game: GameKind;
  status: string;
  timeControl: string;
  outcome: "win" | "loss" | "draw" | null;
  reason: string | null;
  at: number;
  opponent: { username: string | null; firstName: string | null; photoUrl: string | null } | null;
}

export function History() {
  const { setScreen } = useStore();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [stats, setStats] = useState({ total: 0, wins: 0, losses: 0, draws: 0 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .history()
      .then((res) => {
        if (cancelled) return;
        setRows(res.matches as Row[]);
        setStats(res.stats);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "не загрузилось");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <motion.div
      className="stack"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="row">
        <button
          className="btn"
          style={{ width: "auto", padding: "8px 14px" }}
          onClick={() => {
            haptic.tap();
            setScreen("lobby");
          }}
        >
          ← Назад
        </button>
        <div style={{ fontWeight: 700, fontSize: 18 }}>История</div>
      </div>

      <div className="stat-row">
        <div className="stat">
          <b>{stats.total}</b>
          <span className="muted small">партий</span>
        </div>
        <div className="stat">
          <b style={{ color: "var(--green)" }}>{stats.wins}</b>
          <span className="muted small">побед</span>
        </div>
        <div className="stat">
          <b style={{ color: "var(--red)" }}>{stats.losses}</b>
          <span className="muted small">поражений</span>
        </div>
        <div className="stat">
          <b>{stats.draws}</b>
          <span className="muted small">ничьих</span>
        </div>
      </div>

      {error && <div className="muted small">{error}</div>}

      {rows === null && !error && (
        <div className="center" style={{ padding: 30 }}>
          <div className="spinner" />
        </div>
      )}

      {rows?.length === 0 && (
        <div className="muted small" style={{ textAlign: "center", padding: 24 }}>
          Пока пусто. Сыграй первую партию!
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        {rows?.map((r, i) => {
          const name = r.opponent?.firstName ?? r.opponent?.username ?? "Соперник";
          const label = r.outcome === "win" ? "П" : r.outcome === "loss" ? "×" : r.outcome === "draw" ? "=" : "…";
          return (
            <motion.div
              key={r.id}
              className="history-row"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.025, 0.3) }}
            >
              <span className={`history-badge ${r.outcome ?? "draw"}`}>{label}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontWeight: 600 }}>
                  {GAME_NAME[r.game] ?? r.game}
                </span>
                <span className="muted small">
                  {name} · {r.timeControl}
                  {r.reason ? ` · ${REASON[r.reason] ?? r.reason}` : ""}
                </span>
              </span>
              <span className="muted small" style={{ flex: "none" }}>
                {new Date(r.at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}
              </span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
